import * as THREE from "three";
import { HqMaterials } from "./hqMaterials";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";

/** The reward chest stage's pieces (chestStage.ts): the lid's lit inside, the contact shadow and the rays behind it. */

/**
 * The open lid leans back past upright, so its flat inner face sits square to the camera right behind the mouth. It
 * is baked with the same mid-grey wood as the rest of the chest and took the key light and the studio environment
 * head-on, so under the shaft it read as a pale slab (with the shaft's blades, the "pale translucent square"). The
 * lid's own meshes get a copy of their material in which faces pointing down in the lid's space (the inside of a
 * closed lid) are darkened and lit by the rank's colour from the shaft instead.
 */
export function tintLidInside(root: THREE.Object3D, rarity: string, glow: { value: THREE.Color }): void {
  const lid = root.getObjectByName(`chest_${rarity}lid`) ?? root.getObjectByName(`chest_${rarity}.lid`);
  if (!lid) return;
  const copies = new Map<THREE.Material, THREE.Material>();
  const visit = (o: THREE.Object3D): void => {
    for (const c of o.children) {
      if (!(c as THREE.Mesh).isMesh) {
        // a multi-primitive mesh loads as an unnamed group of meshes; the named child parts (the lock, the lid's gem)
        // are not the lid
        if (!c.name.startsWith(`chest_${rarity}`)) visit(c);
        continue;
      }
      const mesh = c as THREE.Mesh;
      const src = mesh.material as THREE.MeshStandardMaterial;
      if (Array.isArray(src) || !src.isMeshStandardMaterial) continue;
      let m = copies.get(src);
      if (!m) {
        const lm = src.clone();
        const prev = src.onBeforeCompile, prevKey = src.customProgramCacheKey?.() ?? "";
        lm.onBeforeCompile = (sh, r) => {
          prev.call(src, sh, r);
          sh.uniforms.uLidGlow = glow;
          sh.vertexShader = sh.vertexShader
            .replace("#include <common>", "#include <common>\nvarying float vLidUp;")
            .replace("#include <beginnormal_vertex>", "#include <beginnormal_vertex>\nvLidUp = objectNormal.y / max(1e-6, length(objectNormal));");
          sh.fragmentShader = sh.fragmentShader
            .replace("#include <common>", "#include <common>\nvarying float vLidUp;\nuniform vec3 uLidGlow;")
            .replace("#include <emissivemap_fragment>", `#include <emissivemap_fragment>
              float lidIn = 1.0 - smoothstep(-0.9, -0.55, vLidUp);
              diffuseColor.rgb *= 1.0 - 0.65 * lidIn;
              totalEmissiveRadiance += uLidGlow * lidIn;`);
        };
        lm.customProgramCacheKey = () => `${prevKey}-chestlid`;
        copies.set(src, lm);
        m = lm;
      }
      mesh.material = m;
    }
  };
  visit(lid);
}

/**
 * The stage's light: SHADING.md §3 minus shadows — a warm key from front-right-top, a cool rim from behind, a cool/violet
 * hemisphere — and a small studio environment for the gold and the gems (metal renders black without one; the level's
 * materials keep envMapIntensity 0 in their own scene, so it only shows here).
 */
export function stageLights(scene: THREE.Scene, renderer: THREE.WebGLRenderer): void {
  const key = new THREE.DirectionalLight(0xfff1dc, 1.8);
  key.position.set(-1.6, 2.4, 1.8);
  const rim = new THREE.DirectionalLight(0xbcd2ff, 0.9);
  rim.position.set(1.5, 1.8, -2.4);
  scene.add(key, rim, new THREE.HemisphereLight(0xcfe3ff, 0x6b5a8a, 0.6));
  const pmrem = new THREE.PMREMGenerator(renderer), room = new RoomEnvironment();
  scene.environment = pmrem.fromScene(room, 0.04).texture;
  pmrem.dispose();
  room.dispose();                                            // only its baked texture is kept
}

/** A soft contact shadow so the chest sits on the parchment instead of floating over it. */
export function groundShadow(): THREE.Mesh {
  const c = document.createElement("canvas"); c.width = c.height = 128;
  const g = c.getContext("2d")!; const r = g.createRadialGradient(64, 64, 8, 64, 64, 64);
  r.addColorStop(0, "rgba(40,20,5,0.55)"); r.addColorStop(0.6, "rgba(40,20,5,0.25)"); r.addColorStop(1, "rgba(40,20,5,0)");
  g.fillStyle = r; g.fillRect(0, 0, 128, 128);
  const tex = new THREE.CanvasTexture(c);
  const m = new THREE.Mesh(new THREE.PlaneGeometry(1.9, 1.5), new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false }));
  m.rotation.x = -Math.PI / 2; m.position.y = 0.005; m.renderOrder = -1;
  return m;
}

/** The radiant burst behind the chest (the reference gif): rotating rays + a soft core, camera-facing, additive. */
export function raysMesh(c: readonly number[]): THREE.Mesh {
  const mat = new THREE.ShaderMaterial({
    // depth-tested: transparent objects draw after the opaque chest whatever their renderOrder, so without the test the
    // rays streaked across the chest itself
    transparent: true, depthWrite: false, depthTest: true, blending: THREE.AdditiveBlending, toneMapped: false,
    uniforms: { uTime: HqMaterials.beamTime, uLevel: { value: 0 }, uColor: { value: new THREE.Color(c[0], c[1], c[2]) } },
    vertexShader: `varying vec2 vUv; void main() { vUv = uv - 0.5; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: `varying vec2 vUv; uniform float uTime, uLevel; uniform vec3 uColor;
      float h(float n) { return fract(sin(n) * 43758.5453); }
      void main() {
        float r = length(vUv) * 2.0;                       // 0 centre … 1 edge
        float a = atan(vUv.y, vUv.x);
        float rays = 0.0;
        for (int i = 0; i < 3; i++) {
          float k = float(i);
          float f = 7.0 + k * 5.0;
          float s = sin(a * f + uTime * (0.35 + 0.2 * k) * (k == 1.0 ? -1.0 : 1.0) + k * 1.7);
          rays += pow(max(1e-6, s), 6.0 + k * 4.0) * (0.6 - k * 0.15);
        }
        float radial = pow(max(1e-6, 1.0 - r), 1.2);
        float core = pow(max(1e-6, 1.0 - r * 2.2), 2.0);
        float v = (rays * radial * 1.4 + core * 1.6 + radial * 0.25) * uLevel;
        vec3 col = mix(uColor, vec3(1.0), core * 0.8 + 0.15) * v * 1.4;
        gl_FragColor = vec4(col, clamp(v, 0.0, 1.0));
      }`,
  });
  const m = new THREE.Mesh(new THREE.PlaneGeometry(5.2, 5.2), mat);
  m.renderOrder = -2;
  return m;
}

/**
 * A guard for the reward chests' `open` clip. The 2026-10-01 export (b7da168) keyed the lid's swing negative: past its
 * little lift it turned −100° about its hinge, down behind the chest and into the floor (M30 #7). art/hq_anim.py now
 * keys it positive (up and back to ~118°, settling at ~106°) and every chest GLB and KTX2 twin was re-exported (M31),
 * so for the shipped art this returns the clips untouched (test/chestLid.test.ts checks both). Should an inverted clip
 * come back (an old cached GLB, a re-export from an older script), it is returned with its lid keys turned to the same
 * swing upward (|angle| about the hinge, x), every other track left alone.
 */
export function lidOpensUp(clips: readonly THREE.AnimationClip[], rarity: string): THREE.AnimationClip[] {
  return clips.map((clip) => {
    if (clip.name !== "open") return clip;
    const lidTrack = clip.tracks.find((t) => t.name === `chest_${rarity}lid.quaternion` || t.name === `chest_${rarity}.lid.quaternion`);
    if (!lidTrack) return clip;
    const v = lidTrack.values, n = v.length / 4;
    // the hinge angle of the last key, with w ≥ 0 (q and −q are one rotation)
    const angleAt = (k: number) => { const s = v[k * 4 + 3] < 0 ? -1 : 1; return 2 * Math.atan2(s * v[k * 4], s * v[k * 4 + 3]); };
    if (angleAt(n - 1) >= 0) return clip;
    const out = clip.clone();
    const t = out.tracks.find((x) => x.name === lidTrack.name)!;
    const values = Float32Array.from(t.values);
    for (let k = 0; k < n; k++) {
      const a = Math.abs(angleAt(k));
      values[k * 4] = Math.sin(a / 2); values[k * 4 + 1] = 0; values[k * 4 + 2] = 0; values[k * 4 + 3] = Math.cos(a / 2);
    }
    t.values = values;
    return out;
  });
}

/** When the burst lands in an open clip of `duration` s: the lock snapping as the lid swings (40 % in), never after `latest`. */
export function burstTime(duration: number, latest: number): number {
  return duration > 0 ? Math.min(latest, duration * 0.4) : latest;
}

/**
 * The end screen's card framing (M38, Kevin: "the chest on the level completion screen looks tiny compared to the one
 * when you kill the rare"). The stage's camera is set up for the full-screen reveal, where the burst, the shaft and the
 * cards need the room around the chest; in the end screen's card the same view left the closed chest a speck in the
 * middle of the well (about a quarter of its width). `cardWindow` is the part of that view to show instead: a window
 * centred on the chest's projected box (NDC, −1..1, y up) and `zoom` times smaller than the view, the zoom set so the
 * box fills `fill` of the window on its tighter axis (never below 1: a chest that already fills the view stays put).
 * Fractions of the full view, top-left origin, for `PerspectiveCamera.setViewOffset`.
 */
export interface NdcBox { minX: number; maxX: number; minY: number; maxY: number }
export function cardWindow(box: NdcBox, fill: number): { x: number; y: number; w: number; h: number; zoom: number } {
  const bw = Math.max(1e-3, (box.maxX - box.minX) / 2), bh = Math.max(1e-3, (box.maxY - box.minY) / 2);   // view fractions
  const zoom = Math.max(1, Math.min(fill / bw, fill / bh));
  const w = 1 / zoom, h = 1 / zoom;
  const cx = (box.minX + box.maxX) / 4 + 0.5, cy = 0.5 - (box.minY + box.maxY) / 4;   // the box's centre, top-left origin
  // the window stays inside the view, so nothing outside the rendered frustum is asked for
  const x = Math.max(0, Math.min(1 - w, cx - w / 2)), y = Math.max(0, Math.min(1 - h, cy - h / 2));
  return { x, y, w, h, zoom };
}
